import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { jsPDF } from 'jspdf';
import { securityObjectivesApi } from '../services/api';
import { useToast } from '../hooks/useToast';
import { useSwal } from '../hooks/useSwal';
import { PageHeader } from '../components/PageHeader';
import { NimbusTable } from '../components/NimbusTable';
import { SortableTh } from '../components/SortableTh';
import { useSortableData } from '../hooks/useSortableData';
import { usePagination } from '../hooks/usePagination';
import { Pagination } from '../components/Pagination';
import { SEVERITY_TONE, toneFromMap, type Tone } from '../constants/tone';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SecurityObjective = {
  objectiveId: string;
  title: string;
  description?: string;
  category?: string;
  ownerId?: string;
  ownerName?: string;
  priority?: string;
  targetDate?: string;
  progress?: number;
  status?: string;
  howMeasured?: string;
  resourcesRequired?: string;
  notes?: string;
  trackingMode?: string;   // 'progress' | 'metric'
  metricLabel?: string;
  metricTarget?: number;
  metricCurrent?: number;
  metricUnit?: string;
};

// ---------------------------------------------------------------------------
// Option lists
// ---------------------------------------------------------------------------

const CATEGORIES = [
  'Access Control',
  'Asset Management',
  'Awareness & Training',
  'Business Continuity',
  'Compliance',
  'Cryptography',
  'Human Resources',
  'Incident Response',
  'Network Security',
  'Physical Security',
  'Risk Management',
  'Supplier & Third Party',
  'Vulnerability Management',
  'Other',
];

const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];

const STATUSES = [
  'Not Started',
  'In Progress',
  'On Track',
  'At Risk',
  'Completed',
  'Cancelled',
];

// ---------------------------------------------------------------------------
// Progress helpers
// ---------------------------------------------------------------------------

const getItemProgress = (item: SecurityObjective): number => {
  if (item.trackingMode === 'metric') {
    if (!item.metricTarget || item.metricTarget === 0) return 0;
    return Math.min(100, Math.max(0, Math.round(((item.metricCurrent ?? 0) / item.metricTarget) * 100)));
  }
  return item.progress ?? 0;
};

// ---------------------------------------------------------------------------
// Tone helpers — return one of np-tone-{success,warning,danger,info,primary,neutral}
// ---------------------------------------------------------------------------

// Lifecycle status, not a severity scale - genuinely its own vocabulary, so it stays
// page-local. Rewritten onto the shared toneFromMap() helper for implementation-shape
// consistency only - every tone below is unchanged from before.
const STATUS_TONE: Record<string, Tone> = {
  Completed: 'success',
  'On Track': 'info',
  'In Progress': 'primary',
  'At Risk': 'danger',
  Cancelled: 'neutral',
};
const getStatusTone = (status?: string): Tone => toneFromMap(STATUS_TONE, status, 'warning'); // default: Not Started

// Disclosed recolour (Phase 7, docs/Frontend-Redesign-Plan.md): Critical/High/Medium
// already matched the shared SEVERITY_TONE scale exactly; only Low changes, from this
// file's own neutral to SEVERITY_TONE's success, so all 4 levels stay visually distinct.
const getPriorityTone = (priority?: string): Tone => toneFromMap(SEVERITY_TONE, priority, 'neutral');

const getProgressTone = (progress: number, status?: string) => {
  if (status === 'Completed') return 'success';
  if (status === 'At Risk')   return 'danger';
  if (progress >= 75)         return 'success';
  if (progress >= 40)         return 'info';
  return 'warning';
};

// ---------------------------------------------------------------------------
// PDF export
// ---------------------------------------------------------------------------

const exportPDF = async (
  objectives: SecurityObjective[],
  stats: { total: number; completed: number; inProgress: number; atRisk: number; notStarted: number },
  setExporting: (v: boolean) => void,
) => {
  setExporting(true);
  try {
    const fmt = (d?: string | null) => {
      if (!d) return '—';
      const dt = new Date(d);
      return isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };
    const esc = (s?: string | null) => (s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    const statusColor = (s?: string) => {
      switch (s) {
        case 'Completed':   return '#28c76f';
        case 'On Track':    return '#00cfe8';
        case 'In Progress': return '#696cff';
        case 'At Risk':     return '#dc3545';
        case 'Cancelled':   return '#6c757d';
        default:            return '#ffc107';
      }
    };
    const priorityColor = (p?: string) => {
      switch (p) {
        case 'Critical': return '#dc3545';
        case 'High':     return '#fd7e14';
        case 'Medium':   return '#00cfe8';
        default:         return '#6c757d';
      }
    };
    const badge = (text: string, bg: string) =>
      `<span style="background:${bg};color:#fff;padding:2px 8px;border-radius:12px;font-size:11px;font-weight:600;white-space:nowrap;">${esc(text)}</span>`;
    const statCard = (label: string, value: number, color: string) =>
      `<div style="display:inline-block;width:calc(20% - 8px);margin:0 4px 8px 0;padding:10px 14px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;vertical-align:top;text-align:center;">` +
      `<span style="font-size:20px;font-weight:700;color:${color};">${value}</span><br>` +
      `<span style="font-size:11px;color:#6b7280;">${label}</span></div>`;

    const rows = objectives.map((obj, i) => {
      const prog = Math.max(0, Math.min(100, obj.progress ?? 0));
      const fillColor = obj.status === 'Completed' ? '#15803d' : obj.status === 'At Risk' ? '#dc2626' : prog >= 75 ? '#15803d' : '#4f46e5';
      const progBar = `<div style="background:#e9ecef;border-radius:4px;height:8px;width:100%;overflow:hidden;">` +
        `<div style="background:${fillColor};width:${prog}%;height:100%;border-radius:4px;"></div>` +
        `</div>` +
        `<div style="font-size:10px;color:#6b7280;margin-top:2px;">${prog}%</div>`;
      const bg = i % 2 === 0 ? '#ffffff' : '#f9fafb';
      const metric = obj.trackingMode === 'metric' && obj.metricLabel
        ? `<div style="font-size:11px;color:#6b7280;margin-top:4px;">${esc(obj.metricLabel)}: <strong>${obj.metricCurrent ?? '—'}/${obj.metricTarget ?? '—'} ${esc(obj.metricUnit)}</strong></div>`
        : '';
      const notes = obj.howMeasured
        ? `<div style="font-size:11px;color:#6b7280;margin-top:2px;">${esc(obj.howMeasured)}</div>`
        : '';
      return `<tr style="background:${bg};border-bottom:1px solid #e5e7eb;">
        <td style="padding:10px 8px;vertical-align:top;width:30%;">
          <div style="font-weight:600;font-size:13px;color:#111827;">${esc(obj.title)}</div>
          ${notes}${metric}
        </td>
        <td style="padding:10px 8px;vertical-align:middle;width:12%;">
          ${obj.category ? `<span style="background:#f3f4f6;color:#374151;padding:2px 8px;border-radius:10px;font-size:11px;">${esc(obj.category)}</span>` : '<span style="color:#9ca3af;">—</span>'}
        </td>
        <td style="padding:10px 8px;vertical-align:middle;width:13%;font-size:12px;color:#374151;">${esc(obj.ownerName)}</td>
        <td style="padding:10px 8px;vertical-align:middle;width:10%;">
          ${obj.priority ? badge(obj.priority, priorityColor(obj.priority)) : '<span style="color:#9ca3af;">—</span>'}
        </td>
        <td style="padding:10px 8px;vertical-align:middle;width:11%;font-size:12px;color:#374151;">${fmt(obj.targetDate)}</td>
        <td style="padding:10px 8px;vertical-align:middle;width:12%;">${progBar}</td>
        <td style="padding:10px 8px;vertical-align:middle;width:12%;">
          ${badge(obj.status || 'Not Started', statusColor(obj.status))}
        </td>
      </tr>`;
    }).join('');

    const genDateSO = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    const html = `<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;background:#ffffff;color:#111827;width:900px;padding:40px;">
      <div style="padding:32px;background:#fafafa;border:1px solid #e5e7eb;border-radius:8px;margin-bottom:32px;">
        <h1 style="font-size:28px;font-weight:800;color:#111827;margin:0 0 12px 0;">Security Objectives</h1>
        <div style="margin-bottom:20px;">
          <span style="padding:4px 12px;border-radius:20px;font-size:12px;font-weight:600;color:#5b21b6;background:#ede9fe;">ISO 27001:2022 \u2014 Clause 6.2</span>
          <span style="font-size:13px;color:#6b7280;margin-left:12px;">Generated ${genDateSO}</span>
        </div>
        ${statCard('Total', stats.total, '#111827')}
        ${statCard('Completed', stats.completed, '#15803d')}
        ${statCard('In Progress', stats.inProgress, '#0891b2')}
        ${statCard('At Risk', stats.atRisk, '#dc2626')}
        ${statCard('Not Started', stats.notStarted, '#6b7280')}
      </div>
      <div style="border:1px solid #e5e7eb;border-radius:8px;margin-bottom:32px;">
        <table style="width:100%;border-collapse:collapse;">
          <thead>
            <tr style="background:#f9fafb;border-bottom:2px solid #e5e7eb;">
              <th style="padding:10px 8px;text-align:left;font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.06em;">Objective</th>
              <th style="padding:10px 8px;text-align:left;font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.06em;">Category</th>
              <th style="padding:10px 8px;text-align:left;font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.06em;">Owner</th>
              <th style="padding:10px 8px;text-align:left;font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.06em;">Priority</th>
              <th style="padding:10px 8px;text-align:left;font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.06em;">Target Date</th>
              <th style="padding:10px 8px;text-align:left;font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.06em;">Progress</th>
              <th style="padding:10px 8px;text-align:left;font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.06em;">Status</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
      <table style="width:100%;border-collapse:collapse;margin-top:32px;"><tr>
        <td style="padding-top:16px;border-top:1px solid #e5e7eb;font-size:11px;color:#9ca3af;">Security Objectives \u00b7 ISO/IEC 27001:2022 \u00a76.2</td>
        <td style="padding-top:16px;border-top:1px solid #e5e7eb;text-align:right;font-size:11px;color:#9ca3af;">Generated ${genDateSO}</td>
      </tr></table>
    </div>`;

    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'position:absolute;top:99999px;left:0;width:900px;';
    wrapper.innerHTML = html;
    document.body.appendChild(wrapper);
    void wrapper.getBoundingClientRect();
    const fullH = wrapper.scrollHeight;

    const { default: html2canvas } = await import('html2canvas');
    const canvas = await html2canvas(wrapper, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff', width: 900, height: fullH, windowWidth: 900, windowHeight: fullH });
    document.body.removeChild(wrapper);

    const margin = 10;
    const printW = 190;
    const imgH = canvas.height / 2;
    const ratio = printW / (canvas.width / 2);
    const docH = imgH * ratio + margin * 2;
    const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: [210, Math.max(docH, 210.1)] });
    pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', margin, margin, printW, imgH * ratio);
    pdf.save(`security-objectives-${new Date().toISOString().slice(0, 10)}.pdf`);
  } catch (err) {
    console.error(err);
  } finally {
    setExporting(false);
  }
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SecurityObjectivesPage: React.FC = () => {
  const qc = useQueryClient();
  const toast = useToast();
  const swal = useSwal();
  const navigate = useNavigate();

  const { data: items = [], isLoading } = useQuery<SecurityObjective[]>({
    queryKey: ['security-objectives'],
    queryFn: securityObjectivesApi.getAll,
  });

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [exporting, setExporting] = useState(false);

  // ---------------------------------------------------------------- mutations

  const deleteMutation = useMutation({
    mutationFn: (objectiveId: string) => securityObjectivesApi.delete(objectiveId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['security-objectives'] });
      toast.success('Objective deleted.');
    },
    onError: (e: Error) => toast.error(`Failed to delete: ${e.message}`),
  });

  const handleDelete = async (item: SecurityObjective) => {
    const confirmed = await swal.confirmDelete(item.title || item.objectiveId);
    if (!confirmed) return;
    deleteMutation.mutate(item.objectiveId);
  };

  // ---------------------------------------------------------------- derived data

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const stats = useMemo(() => ({
    total: items.length,
    completed: items.filter((i) => i.status === 'Completed').length,
    inProgress: items.filter((i) => i.status === 'In Progress' || i.status === 'On Track').length,
    atRisk: items.filter((i) => i.status === 'At Risk').length,
    notStarted: items.filter((i) => !i.status || i.status === 'Not Started').length,
    overdue: items.filter((i) => {
      if (!i.targetDate || i.status === 'Completed' || i.status === 'Cancelled') return false;
      const d = new Date(i.targetDate);
      return !isNaN(d.getTime()) && d < today;
    }).length,
  }), [items]);

  const searched = useMemo(() => {
    const q = search.toLowerCase();
    return items.filter((i) =>
      (!q || [i.title, i.category, i.ownerName, i.description]
        .some((v) => v?.toLowerCase().includes(q)))
      && (!filterStatus || i.status === filterStatus)
      && (!filterCategory || i.category === filterCategory)
      && (!filterPriority || i.priority === filterPriority),
    );
  }, [items, search, filterStatus, filterCategory, filterPriority]);

  const { sorted: filtered, field: sortField, dir: sortDir, toggle: toggleSort } = useSortableData<SecurityObjective, keyof SecurityObjective>(
    searched,
    (i, f) => (f === 'progress' ? (i.progress ?? 0) : String(i[f] ?? '')),
    'title',
  );
  const { pageItems, page, setPage, totalPages, pageSize, totalItems } = usePagination(filtered, 25);

  // ---------------------------------------------------------------- render helpers

  const formatDate = (iso?: string) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const isOverdue = (item: SecurityObjective) => {
    if (!item.targetDate || item.status === 'Completed' || item.status === 'Cancelled') return false;
    const d = new Date(item.targetDate);
    return !isNaN(d.getTime()) && d < today;
  };

  // ---------------------------------------------------------------- render

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-shield-star-line"
        title="Security Objectives"
        colour="primary"
        actions={
          <>
            <button className="np-btn np-btn-ghost" onClick={() => exportPDF(filtered, stats, setExporting)} disabled={exporting}>
              {exporting ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> : <i className="ri-file-pdf-line"></i>}
              {exporting ? 'Generating…' : 'Export PDF'}
            </button>
            <button className="np-btn np-btn-primary" onClick={() => navigate('/security-objectives/new')}>
              <i className="ri-add-line"></i>Add Objective
            </button>
          </>
        }
      />

      <div className="np-card">
        {/* Card header: title + stats */}
        <div className="np-card-head flex-wrap">
          <div className="d-flex align-items-center gap-2 me-auto">
            <i className="ri-shield-star-line text-primary"></i>
            <h2>ISO 27001 Security Objectives</h2>
            <span className="np-chip np-tone-neutral np-tabular">{stats.total}</span>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {stats.completed > 0 && (
              <span className="np-chip np-tone-success">
                <i className="ri-checkbox-circle-line"></i>{stats.completed} completed
              </span>
            )}
            {stats.inProgress > 0 && (
              <span className="np-chip np-tone-primary">
                <i className="ri-loader-line"></i>{stats.inProgress} in progress
              </span>
            )}
            {stats.atRisk > 0 && (
              <span className="np-chip np-tone-danger">
                <i className="ri-error-warning-line"></i>{stats.atRisk} at risk
              </span>
            )}
            {stats.overdue > 0 && (
              <span className="np-chip np-tone-danger">
                <i className="ri-time-line"></i>{stats.overdue} overdue
              </span>
            )}
            {stats.notStarted > 0 && (
              <span className="np-chip np-tone-warning">
                <i className="ri-pause-circle-line"></i>{stats.notStarted} not started
              </span>
            )}
          </div>
        </div>

        {/* Filters toolbar */}
        <div className="np-card-body border-bottom">
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <div className="np-search" style={{ maxWidth: 280 }}>
              <i className="ri-search-line"></i>
              <input
                type="text"
                placeholder="Search objectives…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button className="np-icon-btn" onClick={() => setSearch('')}><i className="ri-close-line"></i></button>
              )}
            </div>
            <select
              className="np-select"
              style={{ maxWidth: 160 }}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">All Statuses</option>
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
            <select
              className="np-select"
              style={{ maxWidth: 170 }}
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <option value="">All Categories</option>
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <select
              className="np-select"
              style={{ maxWidth: 140 }}
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
            >
              <option value="">All Priorities</option>
              {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
            </select>
            {(search || filterStatus || filterCategory || filterPriority) && (
              <button
                className="np-btn np-btn-ghost np-btn-sm"
                onClick={() => { setSearch(''); setFilterStatus(''); setFilterCategory(''); setFilterPriority(''); }}
              >
                <i className="ri-filter-off-line"></i>Clear
              </button>
            )}
            <span className="np-sub ms-auto">
              {filtered.length} result{filtered.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Table */}
        <div className="np-card-body p-0">
          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
              <div className="np-sub mt-2">Loading objectives…</div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-5">
              <i className="ri-shield-star-line ri-3x d-block mb-2 opacity-25"></i>
              <p className="np-sub mb-0">
                {items.length === 0
                  ? 'No security objectives yet — add one to get started.'
                  : 'No results match your filters.'}
              </p>
            </div>
          ) : (
            <NimbusTable thead={<tr>
                  {(
                    [
                      ['title',     'Objective'],
                      ['category',  'Category'],
                      ['ownerName', 'Owner'],
                      ['priority',  'Priority'],
                      ['targetDate','Target Date'],
                      ['progress',  'Progress'],
                      ['status',    'Status'],
                    ] as [keyof SecurityObjective, string][]
                  ).map(([field, label]) => (
                    <SortableTh key={field} field={field} active={sortField === field} dir={sortDir} onSort={toggleSort}>
                      {label}
                    </SortableTh>
                  ))}
                  <th style={{ width: 80 }}></th>
                </tr>}>
                {pageItems.map((item) => {
                  const prog = getItemProgress(item);
                  const overdue = isOverdue(item);
                  return (
                    <tr key={item.objectiveId}>
                      <td style={{ minWidth: 200 }}>
                        <div className="fw-semibold">{item.title}</div>
                        {item.description && (
                          <div className="np-sub text-truncate" style={{ maxWidth: 240 }} title={item.description}>
                            {item.description}
                          </div>
                        )}
                      </td>
                      <td>
                        {item.category
                          ? <span className="np-chip np-tone-neutral">{item.category}</span>
                          : <span className="np-sub">—</span>}
                      </td>
                      <td>{item.ownerName || <span className="np-sub">—</span>}</td>
                      <td>
                        <span className={`np-chip np-tone-${getPriorityTone(item.priority)}`}>
                          {item.priority || '—'}
                        </span>
                      </td>
                      <td>
                        {item.targetDate ? (
                          <span className={`np-tabular${overdue ? ' text-danger fw-semibold' : ''}`}>
                            {overdue && <i className="ri-alarm-warning-line me-1"></i>}
                            {formatDate(item.targetDate)}
                          </span>
                        ) : (
                          <span className="np-sub">—</span>
                        )}
                      </td>
                      <td style={{ minWidth: 130 }}>
                        <div className="d-flex align-items-center gap-2">
                          <div className="np-progress flex-grow-1">
                            <div
                              className={`np-fill np-tone-${getProgressTone(prog, item.status)}`}
                              style={{ width: `${prog}%` }}
                            ></div>
                          </div>
                          <span className="np-sub np-tabular" style={{ minWidth: 32 }}>{prog}%</span>
                        </div>
                        {item.trackingMode === 'metric' && item.metricTarget ? (
                          <div className="np-hint np-tabular" style={{ marginTop: 2 }}>
                            {item.metricCurrent ?? 0}{item.metricUnit ? ` ${item.metricUnit}` : ''} / {item.metricTarget}{item.metricUnit ? ` ${item.metricUnit}` : ''}{item.metricLabel ? ` ${item.metricLabel}` : ''}
                          </div>
                        ) : null}
                      </td>
                      <td>
                        <span className={`np-chip np-tone-${getStatusTone(item.status)}`}>
                          {item.status || 'Not Started'}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <button
                            className="np-icon-btn"
                            title="Edit"
                            onClick={() => navigate(`/security-objectives/${item.objectiveId}/edit`)}
                          >
                            <i className="ri-pencil-line"></i>
                          </button>
                          <button
                            className="np-icon-btn np-icon-btn-danger"
                            title="Delete"
                            onClick={() => handleDelete(item)}
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </NimbusTable>
          )}
          {pageItems.length > 0 && (
            <Pagination page={page} totalPages={totalPages} pageSize={pageSize} totalItems={totalItems} onPageChange={setPage} />
          )}
        </div>
      </div>
    </div>
  );
};

